import API_BASE, { getAuthHeaders } from "./api";

export class UserService {
    // Get current user profile
    static async getCurrentUser() {
        const response = await fetch(`${API_BASE}/users/profile`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to get user profile');
        }

        return data.data;
    }

    // Update user profile
    static async updateProfile(profileData) {
        const response = await fetch(`${API_BASE}/users/profile`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(profileData),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to update profile');
        }

        return data.data;
    }

    // Change password
    static async changePassword(passwordData) {
        const response = await fetch(`${API_BASE}/users/change-password`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(passwordData),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to change password');
        }

        return data;
    }

    // Update preferences
    static async updatePreferences(preferences) {
        const response = await fetch(`${API_BASE}/users/preferences`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(preferences),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to update preferences');
        }

        return data.data;
    }

    // Toggle newsletter
    static async toggleNewsletter() {
        const response = await fetch(`${API_BASE}/users/newsletter`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to update newsletter subscription');
        }

        return data.data;
    }

    // Address management
    static async getAddresses() {
        const response = await fetch(`${API_BASE}/users/addresses`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to get addresses');
        }

        return data.data;
    }

    static async getAddressById(addressId) {
        const response = await fetch(`${API_BASE}/users/addresses/${addressId}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to get address');
        }

        return data.data;
    }

    static async addAddress(addressData) {
        const response = await fetch(`${API_BASE}/users/addresses`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(addressData),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to add address');
        }

        return data.data;
    }

    static async updateAddress(addressId, addressData) {
        const response = await fetch(`${API_BASE}/users/addresses/${addressId}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(addressData),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to update address');
        }

        return data.data;
    }

    static async deleteAddress(addressId) {
        const response = await fetch(`${API_BASE}/users/addresses/${addressId}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to delete address');
        }

        return data;
    }

    static async setDefaultAddress(addressId) {
        const response = await fetch(`${API_BASE}/users/addresses/${addressId}/set-default`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to set default address');
        }

        return data;
    }

    // Device management
    static async getDevices() {
        const response = await fetch(`${API_BASE}/users/devices`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to get devices');
        }

        return data.data;
    }

    static async revokeDevice(tokenId) {
        const response = await fetch(`${API_BASE}/users/devices/${tokenId}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to revoke device');
        }

        return data;
    }

    static async revokeAllDevices() {
        const response = await fetch(`${API_BASE}/users/devices`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to revoke all devices');
        }

        return data;
    }

    // Wishlist management
    static async getWishlist() {
        const response = await fetch(`${API_BASE}/users/wishlist`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to get wishlist');
        }

        return data.data;
    }

    static async addToWishlist(productId) {
        const response = await fetch(`${API_BASE}/users/wishlist`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ productId }),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to add to wishlist');
        }

        return data;
    }

    static async removeFromWishlist(productId) {
        const response = await fetch(`${API_BASE}/users/wishlist/${productId}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to remove from wishlist');
        }

        return data;
    }

    static async clearWishlist() {
        const response = await fetch(`${API_BASE}/users/wishlist`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to clear wishlist');
        }

        return data;
    }

    // Account management
    static async deactivateAccount() {
        const response = await fetch(`${API_BASE}/users/deactivate`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to deactivate account');
        }

        return data;
    }

    // ADMIN: Get all users
    static async getAllUsers(filters = {}) {
        const queryParams = new URLSearchParams(filters).toString();
        const response = await fetch(`${API_BASE}/users${queryParams ? `?${queryParams}` : ''}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to get users');
        }

        return data;
    }

    // ADMIN: Get user by ID
    static async getUserById(userId) {
        const response = await fetch(`${API_BASE}/users/${userId}`, {
            method: 'GET',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to get user');
        }

        return data.data;
    }

    // ADMIN: Update user role
    static async updateUserRole(userId, role) {
        const response = await fetch(`${API_BASE}/users/${userId}/role`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({ role }),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to update user role');
        }

        return data.data;
    }

    // ADMIN: Toggle user active status
    static async toggleUserActive(userId) {
        const response = await fetch(`${API_BASE}/users/${userId}/active`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to toggle user active status');
        }

        return data.data;
    }

    // ADMIN: Delete user
    static async deleteUser(userId) {
        const response = await fetch(`${API_BASE}/users/${userId}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
            credentials: 'include'
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.message || 'Failed to delete user');
        }

        return data;
    }
}